<template>
  <div class="serp-simulator" v-if="isDesktopMode">
    <a :href="fullUrl" v-stop-draggable target="_blank" class="serp-simulator__title">{{title}}</a>
    <a :href="fullUrl" v-stop-draggable target="_blank" class="serp-simulator__url">{{url}}</a>
    <div class="serp-simulator__rating" v-if="isReviewVisible">
      <star-rating
        class="serp-simulator__rating-stars"
        :max-rating="5"
        :increment="0.1"
        inactive-color="#b5c0c9"
        active-color="#ffb82c"
        :star-size="15"
        :show-rating="false"
        :rating="rating"
        :read-only="true"
      />
      <span v-stop-draggable class="serp-simulator__rating-info">
          &nbsp; Rating: {{rating}} - {{reviewCount}} reviews</span>
    </div>
    <div v-stop-draggable class="serp-simulator__description">{{cuttedDescription}}</div>
  </div>
  <div class="serp-simulator serp-simulator_mobile" v-else>
    <a :href="fullUrl" target="_blank" class="serp-simulator__url serp-simulator__url_mobile">
      <img class="serp-simulator__logo" src="@/assets/rfx-logo.svg" alt="Audiencekey">
      {{url}}
    </a>
    <a :href="fullUrl" v-stop-draggable target="_blank" class="serp-simulator__title serp-simulator__title_mobile">{{title}}</a>
    <div class="serp-simulator__description serp-simulator__description_mobile " v-stop-draggable>{{cuttedDescription}}</div>
    <div class="serp-simulator__rating serp-simulator__rating_mobile" v-if="isReviewVisible">
      <span class="serp-simulator__rating-subtitle" v-stop-draggable>Rating:</span>
      <span class="serp-simulator__rating-info" v-stop-draggable>{{rating}}</span>
      <star-rating
        class="serp-simulator__rating-stars"
        :max-rating="5"
        :increment="0.1"
        inactive-color="#b5c0c9"
        active-color="#ffb82c"
        :star-size="15"
        :show-rating="false"
        :rating="rating"
        :read-only="true"
      />
      <span class="serp-simulator__rating-review" v-stop-draggable>({{reviewCount}})</span>
    </div>
  </div>
</template>
<script>
import StarRating from 'vue-star-rating/src/star-rating'
// eslint-disable-next-line no-unused-vars
// import ContentElements from './ContentElements'

const MIN_CUTTED_DESCRIPTION_LENGTH = 165
const MIN_CUTTED_DESCRIPTION_LENGTH_MOBILE = 312
/**
 * SERP simulator. Used in {@link ContentElements}
 */
export default {
  name: 'GoogleSerpSimulator',
  components: { StarRating },
  props: {
    url: {
      type: String
    },
    title: {
      type: String
    },
    fullUrl: {
      type: String
    },
    description: {
      type: String
    },
    mode: {
      type: String,
      default: 'desktop'
    },
    rating: {
      type: Number,
      default: 4
    },
    reviewCount: {
      type: Number,
      default: 50
    },
    isReviewVisible: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    cuttedDescription () {
      if (!this.description) {
        return
      }
      const shouldBeCutted = this.description.length > this.minDescriptionLength
      if (shouldBeCutted) {
        return `${this.description.slice(0, this.minDescriptionLength)}...`
      }
      return this.description
    },
    minDescriptionLength () {
      return this.isDesktopMode ? MIN_CUTTED_DESCRIPTION_LENGTH : MIN_CUTTED_DESCRIPTION_LENGTH_MOBILE
    },
    isDesktopMode () {
      return this.mode === 'desktop'
    }
  }
}
</script>

<style scoped lang="scss">

  .serp-simulator {
    display: flex;
    flex-direction: column;
    border-radius: 4px;
    border: 1px solid $rfx-light-grey;
    padding: 20px;
    background: white;

    &__logo {
      margin-right: 5px;
      width: 18px;
    }

    &_mobile {
      font-size: 12px;
    }

    &__rating {
      display: flex;
      align-items: center;
      margin-top: 5px;

      &_mobile {
        max-width: 128px;
        display: grid;
        grid-template-columns: auto auto auto;
        grid-template-areas: "subtitle subtitle subtitle" "rating stars review";
        grid-gap: 7px 5px;
        color: $rfx-brownish-grey;
        margin-top: 7px;
      }

      &-info {
        color: $rfx-brownish-grey;
        grid-area: rating;
      }

      &-review {
        color: $rfx-brownish-grey;
        grid-area: review;
      }

      &-stars {
        display: inline-block;
        grid-area: stars;
      }

      &-subtitle {
        font-size: 12px;
        color: $rfx-dark-march;
        font-weight: 500;
        grid-area: subtitle;
      }
    }

    &__title {
      font-size: 20px;
      color: $rfx-bright-blue !important;
      text-decoration: none;
      max-width: 601px;
      text-overflow: ellipsis;
      overflow: hidden;
      box-sizing: border-box;
      white-space: nowrap;

      &_mobile {
        font-size: 14px;
        max-width: 300px;
        margin: 7px 0 3px 0;
      }
    }

    &__url {
      font-size: 16px;
      color: $rfx-green !important;
      text-decoration: none;
      display: flex;
      box-sizing: border-box;
      margin-top: 7px;
      text-overflow: ellipsis;
      overflow: hidden;
      white-space: nowrap;
      max-width: 490px;
      align-items: center;

      &_mobile {
        font-size: 10px;
        max-width: 300px;
      }

      &::after {
        width: 0;
        height: 0;
        display: block;
        content: '';
        margin: 7px 3px 0;
        border-left: 5px solid transparent;
        border-right: 5px solid transparent;
        border-top: 4px solid $rfx-green;
      }
    }

    &__description {
      font-size: 16px;
      margin-top: 4px;
      line-height: 1.2em;
      max-height: 2.4em;
      color: $rfx-medium-grey;
      text-decoration: none;
      text-overflow: ellipsis;
      overflow: hidden;
      box-sizing: border-box;
      display: block;
      max-width: 688px;
      position: relative;

      &_mobile {
        font-size: 12px;
        max-width: 300px;
        max-height: 7em;
      }

    }
  }

</style>
